import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from 'react';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import type { AspectRatio } from '../../../shared/platforms';
import { Button, Dialog } from '../ui';
import { cn } from '../../lib/utils';

export interface RenditionCropToolProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  aspect: AspectRatio;
  platformLabel: string;
  busy?: boolean;
  onConfirm: (dataUrl: string, width: number, height: number) => void;
}

/** Preview frame width — height follows the aspect ratio. */
const FRAME_WIDTH = 340;
const MAX_ZOOM = 3;

interface Size {
  w: number;
  h: number;
}

interface Transform {
  /** Extra zoom on top of the "cover" scale, 1..MAX_ZOOM. */
  zoom: number;
  /** Pan offset in preview-frame pixels (top-left of the image). */
  x: number;
  y: number;
}

/**
 * Client-side crop tool: a fixed-ratio frame the image is panned and zoomed
 * behind, matching `aspect` exactly. Confirming rasterizes the same transform
 * onto an offscreen canvas at the platform's recommended export dimensions.
 *
 * All scale math is a pure function of the frame size + the image's natural
 * size, computed fresh on every call — nothing is cached across the async gap
 * between "image loaded" and "state committed", which is what makes the very
 * first frame line up correctly.
 */
export function RenditionCropTool({
  open,
  onOpenChange,
  imageUrl,
  aspect,
  platformLabel,
  busy,
  onConfirm,
}: RenditionCropToolProps) {
  const frameHeight = Math.round(FRAME_WIDTH / aspect.ratio);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = useState<Size | null>(null);
  const [transform, setTransform] = useState<Transform>({ zoom: 1, x: 0, y: 0 });
  const dragState = useRef<{ startX: number; startY: number; origin: Transform } | null>(null);

  // Reset the crop whenever a new image/aspect combination opens.
  useEffect(() => {
    if (open) {
      setNatural(null);
      setTransform({ zoom: 1, x: 0, y: 0 });
    }
  }, [open, imageUrl, aspect.id]);

  /** "Cover" scale at zoom=1, plus whatever extra zoom is dialed in. */
  const scaleFor = (size: Size, zoom: number): number =>
    Math.max(FRAME_WIDTH / size.w, frameHeight / size.h) * zoom;

  const clamp = (next: Transform, size: Size): Transform => {
    const scale = scaleFor(size, next.zoom);
    const dw = size.w * scale;
    const dh = size.h * scale;
    const minX = Math.min(0, FRAME_WIDTH - dw);
    const minY = Math.min(0, frameHeight - dh);
    return {
      zoom: next.zoom,
      x: Math.min(0, Math.max(minX, next.x)),
      y: Math.min(0, Math.max(minY, next.y)),
    };
  };

  const centerTransform = (zoom: number, size: Size): Transform => {
    const scale = scaleFor(size, zoom);
    const dw = size.w * scale;
    const dh = size.h * scale;
    return clamp({ zoom, x: (FRAME_WIDTH - dw) / 2, y: (frameHeight - dh) / 2 }, size);
  };

  const onImageLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const size: Size = { w: img.naturalWidth, h: img.naturalHeight };
    setNatural(size);
    setTransform(centerTransform(1, size));
  };

  const setZoom = (zoom: number) => {
    if (!natural) return;
    const clamped = Math.min(MAX_ZOOM, Math.max(1, zoom));
    setTransform((current) => {
      // Zoom around the frame center so the subject does not jump.
      const prevScale = scaleFor(natural, current.zoom);
      const nextScale = scaleFor(natural, clamped);
      const cx = FRAME_WIDTH / 2;
      const cy = frameHeight / 2;
      const nx = cx - ((cx - current.x) / prevScale) * nextScale;
      const ny = cy - ((cy - current.y) / prevScale) * nextScale;
      return clamp({ zoom: clamped, x: nx, y: ny }, natural);
    });
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!natural) return;
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    dragState.current = { startX: event.clientX, startY: event.clientY, origin: transform };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragState.current || !natural) return;
    const { startX, startY, origin } = dragState.current;
    const next = {
      zoom: origin.zoom,
      x: origin.x + (event.clientX - startX),
      y: origin.y + (event.clientY - startY),
    };
    setTransform(clamp(next, natural));
  };

  const endDrag = () => {
    dragState.current = null;
  };

  const onWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    if (!natural) return;
    event.preventDefault();
    setZoom(transform.zoom + (event.deltaY > 0 ? -0.15 : 0.15));
  };

  const confirm = () => {
    const img = imgRef.current;
    if (!img || !natural) return;
    const canvas = document.createElement('canvas');
    canvas.width = aspect.width;
    canvas.height = aspect.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const outputScale = aspect.width / FRAME_WIDTH;
    const scale = scaleFor(natural, transform.zoom);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      img,
      transform.x * outputScale,
      transform.y * outputScale,
      natural.w * scale * outputScale,
      natural.h * scale * outputScale,
    );
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    onConfirm(dataUrl, aspect.width, aspect.height);
  };

  const displayScale = natural ? scaleFor(natural, transform.zoom) : 1;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Crop for ${platformLabel}`}
      description={`${aspect.label} · exports at ${aspect.width}×${aspect.height}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={confirm} loading={busy} disabled={!natural}>
            Save crop
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4">
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onWheel={onWheel}
          className="relative touch-none select-none overflow-hidden rounded-lg border border-border bg-sunken"
          style={{ width: FRAME_WIDTH, height: frameHeight, cursor: natural ? 'grab' : 'default' }}
        >
          {/* eslint-disable-next-line jsx-a11y/alt-text -- decorative crop source, alt text is edited separately */}
          <img
            ref={imgRef}
            src={imageUrl}
            onLoad={onImageLoad}
            draggable={false}
            className={cn('absolute left-0 top-0 max-w-none origin-top-left', !natural && 'opacity-0')}
            style={
              natural
                ? {
                    transform: `translate(${transform.x}px, ${transform.y}px) scale(${displayScale})`,
                    width: natural.w,
                    height: natural.h,
                  }
                : undefined
            }
          />
          <div className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-inset ring-white/25" />
        </div>

        <div className="flex w-full max-w-xs items-center gap-3">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Zoom out"
            disabled={!natural || transform.zoom <= 1}
            onClick={() => setZoom(transform.zoom - 0.2)}
          >
            <Minus className="size-3.5" />
          </Button>
          <input
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={transform.zoom}
            disabled={!natural}
            onChange={(event) => setZoom(Number(event.target.value))}
            className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-sunken accent-[var(--color-accent)]"
            aria-label="Zoom"
          />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Zoom in"
            disabled={!natural || transform.zoom >= MAX_ZOOM}
            onClick={() => setZoom(transform.zoom + 0.2)}
          >
            <Plus className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Reset crop"
            disabled={!natural}
            onClick={() => natural && setTransform(centerTransform(1, natural))}
          >
            <RotateCcw className="size-3.5" />
          </Button>
        </div>
        <p className="text-center text-xs text-subtle">
          Drag to reposition, scroll or use the slider to zoom.
        </p>
      </div>
    </Dialog>
  );
}
